import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { createSelector, Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { AccountSelectors } from '@recruitee/account-data';
import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

const canAccessRequisitions = createSelector(
  AccountSelectors.areRequisitionsEnabled,
  RequisitionsConfigSelectors.isLoaded,
  RequisitionsConfigSelectors.requisitionsFeatureEnabled,
  AbilitiesSelectors.hasOne(Ability.ViewRequisitions),
  (enabledInAccount, requisitionsConfigLoaded, enabledInConfig, hasAbility) => {
    const isEnabled = requisitionsConfigLoaded ? enabledInConfig : enabledInAccount;
    return isEnabled && hasAbility;
  },
);

export const selectCanAccessRequisitions: (store: Store) => Observable<boolean> = (store: Store) =>
  store.select(canAccessRequisitions);

@Injectable({
  providedIn: 'root',
})
export class RequisitionsModuleGuard {
  constructor(
    private store: Store,
    private featureGuard: FeatureGuardFacade,
    private router: Router,
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([
      this.featureGuard.checkFeatureAvailability(GuardedFeature.Requisitions),
      selectCanAccessRequisitions(this.store),
    ]).pipe(
      map(
        ([hasFeature, canAccess]) =>
          (hasFeature && canAccess) || this.router.createUrlTree(['/dashboard']),
      ),
    );
  }
}
